import React, { useState, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  IconButton,
  CircularProgress,
  Button,
  Collapse,
  Menu,
  MenuItem,
  ButtonBase,
  Divider,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCollaborators } from 'store/queries/collaborators/useCollaborators';
import GVUserAvatar from 'components/GVUserAvatar/GVUserAvatar';
import { useUserStateStore } from 'zstore/userStateStore';
import { useSelector, useDispatch } from 'react-redux';
import { getInspectionId, getInspectionOwnerId, getInspectionSettings } from 'store/inspection/inspectionSelectors';
import { updateInspectionSharingStatus } from 'store/request/inspections/actions';
import RestrictedAccessIcon from 'components/icons/ShareInspection/RestrictedAccessIcon';
import TeamAccessIcon from 'components/icons/ShareInspection/TeamAccessIcon';
import LinkIcon from 'components/icons/ShareInspection/LinkIcon';
import CopySuccessIcon from 'components/icons/ShareInspection/CopySuccessIcon';
import WarningIcon from 'components/icons/ShareInspection/WarningIcon';
import SuccessIcon from 'components/icons/ShareInspection/SuccessIcon';
import { shortenInspectionUrl } from 'utils/shortenUrl';
import { getStyleVariables } from 'styles/vars';
import { useTracker } from 'components/Tracker/TrackerProvider';
import GVTextField from 'components/lib/GVTextField/GVTextField';

const useStyles = makeStyles()((theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    dialog: {
      '& .MuiDialog-paper': {
        width: '418px',
        display: 'flex',
        padding: '16px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '24px',
        flexShrink: 0,
        maxWidth: '90vw',
        maxHeight: 'calc(100vh - 120px)',
        borderRadius: '4px',
        border: '0px solid var(--Elevation-04dp, #303236)',
        background: 'var(--Elevation-04dp, #303236)',
        boxShadow:
          '0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
        position: 'absolute',
        margin: 0,
        transform: 'none !important',
      },
      '& .MuiDialog-container': {
        alignItems: 'flex-start',
      },
      '--gds-color-field-background-default': 'var(--gds-color-cool-gray-600)',
    },
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 0,
      width: '100%',
    },
    dialogTitleText: {
      display: 'flex',
      height: '33px',
      flexDirection: 'column',
      justifyContent: 'center',
      flex: '1 0 0',
      color: '#FFF',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '26px',
    },
    closeButton: {
      color: '#FFF',
      padding: 0,
      width: '32px',
      height: '32px',
      flexShrink: 0,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.24)',
      },
    },
    dialogContent: {
      padding: 0,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    userList: {
      width: '100%',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: 0,
      margin: 0,
      overflowY: 'auto',
      maxHeight: 'calc(100vh - 400px)',
      ...styleVariables.scrollBar,
    },
    listItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(1, 0),
      marginLeft: 0,
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flex: '1 0 0',
      minWidth: 0,
    },
    listItemAvatar: {
      margin: 0,
      minWidth: 'unset',
    },
    userEmail: {
      display: 'block',
      width: '211px',
      color: '#D1D5DB',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '18px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0,
    },
    userRole: {
      color: 'var(--surface-white-60, rgba(255, 255, 255, 0.60))',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '18px',
      paddingRight: '9px',
    },
    warningBox: {
      display: 'flex',
      width: '386px',
      padding: '16px',
      alignItems: 'flex-start',
      gap: '18px',
      borderRadius: '8px',
      border: '0px solid #E5E7EB',
      background: 'var(--gds-color-cool-gray-700, #1F2126)',
      marginBottom: '24px',
    },
    warningIcon: {
      color: theme.palette.warning.main,
    },
    warningText: {
      color: 'var(--surface-white-100, #FFF)',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '18px',
      flex: '1 0 0',
    },
    subHeader: {
      display: 'flex',
      width: '386px',
      height: '25px',
      flexDirection: 'column',
      justifyContent: 'center',
      color: '#FFF',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      marginTop: '8px',
    },
    shareInputContainer: {
      display: 'flex',
      width: '386px',
      flexDirection: 'column',
      gap: '4px',
      marginTop: '16px',
      '--gds-spacing-field-container-margin': 'auto',
    },
    inputButtonRow: {
      display: 'flex',
      width: '100%',
      gap: '16px',
      alignItems: 'center',
    },
    shareButton: {
      display: 'flex',
      width: '65px',
      height: '40px',
      padding: '7px 16px 9px 16px',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '4px',
      background: 'var(--primary-600-primary, #EA3632)',
      color: 'var(--surface-white-100, #FFF)',
      opacity: 1,
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      textTransform: 'none',
      '&:hover': {
        background: '#d32f2f',
      },
      '&.Mui-disabled': {
        background: 'var(--surface-white-38, rgba(255, 255, 255, 0.38))',
        color: 'var(--surface-white-38, rgba(255, 255, 255, 0.38))',
      },
    },
    centerContainer: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      padding: theme.spacing(3, 0),
    },
    successBox: {
      display: 'flex',
      width: '100%',
      padding: '8px',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'nowrap',
      borderRadius: '4px',
      border: '1px solid var(--support-38, rgba(46, 210, 171, 0.38))',
      background: 'var(--support-38, rgba(46, 210, 171, 0.38))',
      boxSizing: 'border-box',
    },
    successText: {
      color: '#00E2AC',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      alignSelf: 'stretch',
    },
    errorContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      minHeight: '36px',
      width: '100%',
    },
    errorText: {
      color: 'var(--Primary-400, #F4514E)',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '16px',
      flex: '1 0 0',
      marginTop: '4px',
      wordBreak: 'break-word',
    },
    errorBox: {
      display: 'flex',
      width: '100%',
      padding: '8px',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'nowrap',
      borderRadius: '4px',
      border: '1px solid var(--error-38, rgba(244, 81, 78, 0.38))',
      background: 'var(--error-38, rgba(244, 81, 78, 0.38))',
      boxSizing: 'border-box',
    },
    errorBoxText: {
      color: '#F4514E',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      alignSelf: 'stretch',
    },
    roleButton: {
      display: 'flex',
      padding: '7px 8px 9px 8px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      borderRadius: '4px',
      color: 'var(--lightgrey-100, #8792AB)',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '18px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
    roleButtonActive: {
      backgroundColor: 'rgba(255, 255, 255, 0.24)',
    },
    roleIcon: {
      transition: 'transform 0.3s ease',
    },
    roleIconOpen: {
      transform: 'rotate(180deg)',
    },
    menuItem: {
      display: 'flex',
      padding: '7px 8px 9px 8px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      borderRadius: '4px',
      width: '100%',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
    menuItemText: {
      color: 'var(--surface-white-100, #FFF)',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    menuPaper: {
      padding: '8px',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '24px',
      borderRadius: '4px',
      backgroundColor: 'var(--Elevation-04dp, #303236)',
      boxShadow:
        '0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)',
      minWidth: '120px',
    },
    divider: {
      width: '100%',
      margin: '16px 0',
      borderColor: 'var(--gds-color-cool-gray-500)',
    },
    linkSettingsContainer: {
      display: 'flex',
      width: '100%',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginTop: '8px',
      marginBottom: '8px',
    },
    linkVisibilityButton: {
      display: 'flex',
      padding: '7px 8px 9px 8px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      borderRadius: '4px',
      color: 'var(--lightgrey-100, #8792AB)',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      minWidth: '280px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
    linkVisibilityButtonOpen: {
      backgroundColor: 'var(--State-White-Focus, rgba(255, 255, 255, 0.08))',
    },
    copyLinkButton: {
      display: 'flex',
      padding: '7px 8px 9px 8px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      borderRadius: '4px',
      color: 'var(--surface-white-100, #FFF)',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      position: 'absolute',
      right: 0,
      transition: 'all 0.3s ease-out',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
    copiedLinkText: {
      display: 'flex',
      padding: '7px 8px 9px 8px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      borderRadius: '4px',
      color: 'var(--surface-white-100, #FFF)',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      position: 'absolute',
      right: 0,
      transition: 'all 0.3s ease-out',
      whiteSpace: 'nowrap',
      minWidth: '120px',
    },
    copyLinkContainer: {
      position: 'relative',
      width: '200px',
      height: '34px',
    },
    buttonHidden: {
      opacity: 0,
      transform: 'translateX(-20px)',
      pointerEvents: 'none',
    },
    textHidden: {
      opacity: 0,
      transform: 'translateX(20px)',
      pointerEvents: 'none',
    },
    linkVisibilityMenuItem: {
      display: 'flex',
      width: '100%',
      backgroundColor: 'transparent',
      position: 'relative',
      marginBottom: '12px',
      minHeight: 'unset',
      '&:last-child': {
        marginBottom: 0,
      },
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
      '&.MuiMenuItem-root': {
        padding: 0,
        minHeight: 'unset',
        margin: 0,
      },
    },
    linkVisibilityMenuItemContent: {
      display: 'flex',
      padding: '7px 8px 9px 2px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      width: '100%',
    },
    linkVisibilityMenuItemText: {
      color: 'var(--surface-white-100, #FFF)',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    messageStack: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      gap: 24,
    },
    startSharingButton: {
      display: 'flex',
      padding: '7px 16px 9px 16px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      borderRadius: '4px',
      background: 'var(--primary-600-primary, #EA3632)',
      color: 'var(--surface-white-100, #FFF)',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      textTransform: 'none',
      '&:hover': {
        background: '#d32f2f',
      },
    },
    stopSharingContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      width: '100%',
      marginTop: '24px',
    },
    stopSharingButton: {
      display: 'flex',
      padding: '7px 16px 9px 16px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      borderRadius: '4px',
      color: 'var(--Primary-400, #EA3632)',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      textTransform: 'none',
      border: '1px solid var(--Primary-400, #EA3632)',
      '&:hover': {
        background: '#EA3632',
        color: '#fff',
      },
    },
  };
});

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
}

enum MessageType {
  SUCCESS = 'success',
  ERROR = 'error',
}

const ShareModal: React.FC<ShareModalProps> = ({ open, onClose }) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const inspectionId = useSelector(getInspectionId);
  const inspectionOwnerId = useSelector(getInspectionOwnerId);
  const inspectionSettings = useSelector(getInspectionSettings);
  const tracker = useTracker();
  const {
    collaborators,
    isLoading,
    isShared,
    addCollaborator,
    isAddingCollaborator,
    deleteCollaborator,
    isDeletingCollaborator,
    openToOrganization,
    updateOrganizationAccess,
    isUpdatingOrganizationAccess,
  } = useCollaborators(inspectionId);
  const { user: currentUser } = useUserStateStore();
  const isOwner = currentUser?.sub === inspectionOwnerId;
  const [emailInput, setEmailInput] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState<{ message: string; inBox: boolean }>({ message: '', inBox: false });
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeCollaboratorId, setActiveCollaboratorId] = useState<string | null>(null);
  const [linkVisibilityAnchorEl, setLinkVisibilityAnchorEl] = useState<null | HTMLElement>(null);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const visibleCollaborators = useMemo(() => {
    if (openToOrganization) {
      // Only show the owner when open to the organization
      return collaborators.filter((c) => c.role === 'Owner');
    }
    return collaborators;
  }, [collaborators, openToOrganization]);

  // Calculate dynamic top position based on content height
  const dialogTopPosition = useMemo(() => {
    const collaboratorHeight = collaborators.length * 60;
    const ownerSectionHeight = isOwner ? 150 : 0; // Input fields + link settings
    const estimatedHeight = 200 + collaboratorHeight + ownerSectionHeight;
    if (estimatedHeight < 400) {
      return '200px';
    } else if (estimatedHeight < 600) {
      return '150px';
    } else {
      return '100px';
    }
  }, [collaborators.length]);

  const showMessage = (message: string, type: MessageType, displayInErrorBox = false) => {
    // Clear any existing message and timeout
    setError({ message: '', inBox: false });
    setSuccessMessage('');

    // Show new message after a short delay
    setTimeout(() => {
      if (type === MessageType.SUCCESS) {
        setSuccessMessage(message);
      } else {
        setError({ message, inBox: displayInErrorBox });
      }
      // Focus the input after showing the message (only for non-error box messages)
      if (!displayInErrorBox) {
        emailInputRef.current?.focus();
      }

      // Clear message after 3 seconds
      setTimeout(() => {
        if (type === MessageType.SUCCESS) {
          setSuccessMessage('');
        } else {
          setError({ message: '', inBox: false });
        }
      }, 3000);
    }, 500);
  };

  const handleEmailChange = (value: string | null) => {
    setEmailInput(value ?? '');
    // Clear error when user changes input
    if (error.message) {
      setError({ message: '', inBox: false });
    }
  };

  // Validate that all inputs are valid emails
  const isValidEmails = useMemo(() => {
    if (!emailInput.trim()) return false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = emailInput.split(',').map((email) => email.trim().toLowerCase());

    return emails.length > 0 && emails.every((email) => email !== '' && emailRegex.test(email));
  }, [emailInput]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && isValidEmails && !isAddingCollaborator) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!inspectionId) return;

    // Split, trim, remove duplicates, and convert to lowercase
    const emails = [...new Set(emailInput.split(',').map((email) => email.trim().toLowerCase()))];

    // Check which emails are new vs existing
    const newEmails = emails.filter((email) => !collaborators.some((c) => c.email.toLowerCase() === email));

    // If all emails are existing, show error
    if (newEmails.length === 0) {
      const message =
        emails.length === 1 ? `${emails[0]} has already been added.` : `${emails.join(', ')} have already been added.`;
      showMessage(message, MessageType.ERROR);
      return;
    }

    try {
      const result = await addCollaborator({
        emails: newEmails,
      });

      if (result.success) {
        // Track successful collaborator addition
        tracker.track({
          name: 'collaborator-added',
          data: {
            shared_by_user_id: currentUser?.sub || '',
            shared_with_emails: newEmails.join(','),
          },
        });

        const message =
          newEmails.length === 1
            ? `${newEmails[0]} added! We sent an email to let them know.`
            : newEmails.length > 3
              ? `${newEmails.slice(0, 3).join(', ')} and ${
                  newEmails.length - 3
                } more added! We sent emails to let them know.`
              : `${newEmails.join(', ')} added! We sent emails to let them know.`;
        showMessage(message, MessageType.SUCCESS);
        setEmailInput(''); // Clear the input field after submission
      } else {
        showMessage(result.message || 'Failed to add collaborator(s)', MessageType.ERROR);
      }
    } catch (error) {
      showMessage('An unexpected error occurred while adding collaborator(s)', MessageType.ERROR);
      console.error('Error adding collaborator:', error);
    }
  };

  const handleRoleClick = (event: React.MouseEvent<HTMLElement>, userId: string): void => {
    setMenuAnchorEl(event.currentTarget);
    setActiveCollaboratorId(userId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveCollaboratorId(null);
  };

  const handleRemoveUser = async (email: string) => {
    try {
      // First close the menu
      handleMenuClose();

      // Make the delete call
      const result = await deleteCollaborator({ email });

      if (result.success) {
        // Track successful collaborator removal
        tracker.track({
          name: 'collaborator-removed',
          data: {
            removed_by_user_id: currentUser?.sub || '',
            removed_collaborator_email: email,
          },
        });

        showMessage(`Removed ${email} from collaborators.`, MessageType.SUCCESS);
      } else {
        showMessage(result.message || 'Failed to remove collaborator', MessageType.ERROR, true);
      }
    } catch (error) {
      showMessage('An unexpected error occurred while removing collaborator', MessageType.ERROR, true);
      console.error('Error removing collaborator:', error);
    }
  };

  const handleLinkVisibilityClick = (event: React.MouseEvent<HTMLElement>) => {
    setLinkVisibilityAnchorEl(event.currentTarget);
  };

  const handleLinkVisibilityClose = () => {
    setLinkVisibilityAnchorEl(null);
  };

  const handleLinkVisibilityChange = async (newOpenToOrganization: boolean) => {
    // Close the menu immediately after selection
    handleLinkVisibilityClose();

    // Don't make API call or fire event if the value hasn't changed
    if (newOpenToOrganization === openToOrganization) {
      return;
    }

    try {
      const result = await updateOrganizationAccess(newOpenToOrganization);

      if (result.success) {
        // Track organization access change with new event
        tracker.track({
          name: 'inspection-org-access-changed',
          data: {
            changed_by_user_id: currentUser?.sub || '',
            open_to_organization: newOpenToOrganization,
            collaborator_count: collaborators.length - 1 || 0, // -1 to exclude the owner,
          },
        });
      }

      if (!result.success) {
        showMessage(result.message || 'Failed to update link visibility', MessageType.ERROR, true);
      }
    } catch (error) {
      console.error('Error updating link visibility:', error);
      showMessage('Failed to update link visibility', MessageType.ERROR, true);
    }
  };

  const handleCopyLink = async () => {
    try {
      const url = window.location.href;
      const shortPath = shortenInspectionUrl(url);
      const fullShortUrl = `${window.location.origin}${shortPath}`;
      await navigator.clipboard.writeText(fullShortUrl);
      setIsLinkCopied(true);
      setTimeout(() => {
        setIsLinkCopied(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      showMessage('Failed to copy link to clipboard', MessageType.ERROR);
    }
  };

  const handleShareInspection = async () => {
    try {
      const result = await updateOrganizationAccess(true);
      if (result.success) {
        dispatch(updateInspectionSharingStatus({ settings: { ...inspectionSettings, isSharing: true } }, inspectionId));
        tracker.track({
          name: 'inspection-shared',
          data: {
            shared_by_user_id: currentUser?.sub || '',
          },
        });
      } else {
        showMessage(result.message || 'Failed to share inspection', MessageType.ERROR, true);
      }
    } catch (error) {
      console.error('Error sharing inspection:', error);
      showMessage('An unexpected error occurred', MessageType.ERROR, true);
    }
  };

  const handleStopSharing = async () => {
    handleLinkVisibilityClose();

    try {
      // Revoke collaborator/org access first, then flip isSharing only once that
      // succeeds. The flag fans out to every linked inspection, so flipping it
      // up front (and only reverting the operated inspection on failure) could
      // leave siblings marked unshared while access is still live.
      const collaboratorsToRemove = collaborators.filter((c) => c.role !== 'Owner');
      const results = await Promise.all([
        ...collaboratorsToRemove.map((collaborator) => deleteCollaborator({ email: collaborator.email })),
        updateOrganizationAccess(false),
      ]);
      const allSucceeded = results.every((result) => result.success);

      if (allSucceeded) {
        dispatch(
          updateInspectionSharingStatus({ settings: { ...inspectionSettings, isSharing: false } }, inspectionId),
        );
        showMessage('Sharing has been stopped and all collaborators removed.', MessageType.SUCCESS);
        tracker.track({
          name: 'inspection-unshared',
          data: {
            unshared_by_user_id: currentUser?.sub || '',
          },
        });
      } else {
        const firstError = results.find((result) => !result.success)?.message;
        showMessage(firstError || 'Failed to stop sharing completely.', MessageType.ERROR, true);
      }
    } catch (error) {
      console.error('Error stopping sharing:', error);
      showMessage('An unexpected error occurred while stopping sharing.', MessageType.ERROR, true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="md"
      sx={{
        top: dialogTopPosition,
        transition: 'top 0.3s ease-out',
        zIndex: 10000,
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" className={classes.dialogTitleText}>
          Share with Organization
        </Typography>
        <IconButton aria-label="close" onClick={onClose} className={classes.closeButton} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.messageStack}>
          <Collapse in={successMessage !== ''} timeout={500} mountOnEnter unmountOnExit>
            <Box className={classes.successBox}>
              <SuccessIcon sx={{ width: 20, height: 20 }} />
              <Typography variant="body2" className={classes.successText}>
                {successMessage}
              </Typography>
            </Box>
          </Collapse>
          <Collapse in={error.message !== '' && error.inBox} timeout={500} mountOnEnter unmountOnExit>
            <Box className={classes.errorBox}>
              <WarningIcon sx={{ width: 16, height: 16 }} fillColor="#F4514E" />
              <Typography variant="body2" className={classes.errorBoxText}>
                {error.message}
              </Typography>
            </Box>
          </Collapse>
          {!isShared && (
            <>
              <Box className={classes.warningBox}>
                <WarningIcon sx={{ width: 16, height: 16 }} />
                <Typography variant="body2" className={classes.warningText}>
                  {isOwner
                    ? 'Sharing an inspection locks Prep to keep results consistent. Once shared, the inspection can no longer be edited—even by you. Stop sharing to unlock Prep and make changes.'
                    : 'Only the inspection owner can share the inspection with organization members.'}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Typography variant="subtitle1" className={classes.subHeader}>
          People with Access
        </Typography>

        <List className={classes.userList} data-testid="collaborators-list">
          {isLoading ? (
            <div className={classes.centerContainer}>
              <CircularProgress size={40} />
            </div>
          ) : visibleCollaborators.length === 0 ? (
            <div className={classes.centerContainer}>
              <Typography>No collaborators found</Typography>
            </div>
          ) : (
            visibleCollaborators.map((user, index) => (
              <ListItem
                key={user.id}
                className={classes.listItem}
                data-testid={`collaborator-${user.email.split('@')[0]}`}
              >
                <div className={classes.userInfo}>
                  <ListItemAvatar className={classes.listItemAvatar}>
                    <GVUserAvatar
                      user={{
                        name: user.name || user.email,
                        picture: user.picture,
                      }}
                      className={classes.listItemAvatar}
                    />
                  </ListItemAvatar>
                  <div className={classes.userEmail}>{user.email}</div>
                </div>
                {user.role === 'Owner' || !isOwner ? (
                  <Typography variant="body2" className={classes.userRole}>
                    {user.role}
                  </Typography>
                ) : (
                  <>
                    <ButtonBase
                      className={cx(classes.roleButton, {
                        [classes.roleButtonActive]: activeCollaboratorId === user.id && Boolean(menuAnchorEl),
                      })}
                      onClick={(e) => handleRoleClick(e, user.id)}
                    >
                      {user.role}
                      <KeyboardArrowDownIcon
                        className={`${classes.roleIcon} ${
                          activeCollaboratorId === user.id && menuAnchorEl ? classes.roleIconOpen : ''
                        }`}
                      />
                    </ButtonBase>
                    <Menu
                      anchorEl={menuAnchorEl}
                      open={activeCollaboratorId === user.id && Boolean(menuAnchorEl)}
                      onClose={handleMenuClose}
                      data-testid="collaborator-role-menu"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      sx={{
                        zIndex: 10001,
                      }}
                      slotProps={{
                        paper: {
                          className: classes.menuPaper,
                        },
                      }}
                    >
                      <MenuItem
                        className={classes.menuItem}
                        onClick={() => handleRemoveUser(user.email)}
                        disabled={isDeletingCollaborator}
                        sx={{ width: '100%' }}
                      >
                        <DeleteOutlineIcon sx={{ width: 16, height: 16 }} />
                        <span className={classes.menuItemText}>Remove</span>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </ListItem>
            ))
          )}
        </List>
        {isOwner && !openToOrganization && isShared && (
          <Box className={classes.shareInputContainer}>
            <Box className={classes.inputButtonRow}>
              <GVTextField
                ref={emailInputRef}
                autoFocus
                autoComplete="off"
                placeholder="Enter emails, separated by commas"
                label="Emails"
                isLabelHidden
                data-testid="email-input"
                value={emailInput}
                onValueChange={handleEmailChange}
                onKeyDown={handleKeyDown}
                isDisabled={!isOwner}
                hasError={error.message !== '' && !error.inBox}
              />
              <Button
                className={classes.shareButton}
                data-testid="share-email-button"
                disabled={!isValidEmails || isAddingCollaborator}
                onClick={handleSubmit}
              >
                Add
              </Button>
            </Box>
            <Box className={classes.errorContainer}>
              <Collapse in={error.message !== '' && !error.inBox} timeout={300}>
                <Typography className={classes.errorText}>{error.message}</Typography>
              </Collapse>
            </Box>
          </Box>
        )}
        {isOwner && (
          <>
            <Divider className={classes.divider} />

            {!isShared ? (
              <Box
                data-testid="generate-link-container"
                className={classes.linkSettingsContainer}
                sx={{ justifyContent: 'flex-end' }}
              >
                <ButtonBase className={classes.startSharingButton} onClick={handleShareInspection}>
                  Share Inspection
                </ButtonBase>
              </Box>
            ) : (
              <>
                <Box data-testid="share-scope-dropdown" className={classes.linkSettingsContainer}>
                  <ButtonBase
                    className={`${classes.linkVisibilityButton} ${
                      linkVisibilityAnchorEl ? classes.linkVisibilityButtonOpen : ''
                    }`}
                    onClick={handleLinkVisibilityClick}
                    disabled={isUpdatingOrganizationAccess}
                  >
                    {isUpdatingOrganizationAccess ? (
                      <CircularProgress size={16} sx={{ color: 'var(--lightgrey-100, #8792AB)' }} />
                    ) : (
                      <>
                        {openToOrganization ? (
                          <>
                            <TeamAccessIcon
                              sx={{
                                width: 18,
                                height: 18,
                                aspectRatio: '1/1',
                              }}
                              fillColor="var(--lightgrey-100, #8792AB)"
                            />
                            Anyone in my org can view the link
                          </>
                        ) : (
                          <>
                            <RestrictedAccessIcon
                              sx={{
                                width: 16,
                                height: 16,
                              }}
                              fillColor="var(--lightgrey-100, #8792AB)"
                            />
                            Only people added can view the link
                          </>
                        )}
                        <KeyboardArrowDownIcon
                          className={`${classes.roleIcon} ${linkVisibilityAnchorEl ? classes.roleIconOpen : ''}`}
                        />
                      </>
                    )}
                  </ButtonBase>

                  <Box className={classes.copyLinkContainer}>
                    <ButtonBase
                      className={`${classes.copyLinkButton} ${isLinkCopied ? classes.buttonHidden : ''}`}
                      onClick={handleCopyLink}
                    >
                      <LinkIcon sx={{ width: 16, height: 16 }} />
                      Copy Link
                    </ButtonBase>
                    <Box className={`${classes.copiedLinkText} ${!isLinkCopied ? classes.textHidden : ''}`}>
                      <CopySuccessIcon sx={{ width: 16, height: 16 }} />
                      Link copied!
                    </Box>
                  </Box>
                </Box>

                <Menu
                  anchorEl={linkVisibilityAnchorEl}
                  open={Boolean(linkVisibilityAnchorEl)}
                  onClose={handleLinkVisibilityClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  sx={{
                    zIndex: 10001,
                  }}
                  PaperProps={{
                    sx: {
                      display: 'flex',
                      padding: '8px',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '12px',
                      borderRadius: '4px',
                      backgroundColor: 'var(--Elevation-04dp, #303236)',
                      boxShadow:
                        '0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)',
                      minWidth: '280px',
                      '& .MuiList-root': {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        padding: 0,
                      },
                    },
                  }}
                >
                  <MenuItem className={classes.linkVisibilityMenuItem} onClick={() => handleLinkVisibilityChange(true)}>
                    <Box className={classes.linkVisibilityMenuItemContent}>
                      <TeamAccessIcon
                        sx={{
                          width: 18,
                          height: 18,
                          aspectRatio: '1/1',
                        }}
                      />
                      <span className={classes.linkVisibilityMenuItemText}>Anyone in my org can view the link</span>
                    </Box>
                  </MenuItem>
                  <MenuItem
                    className={classes.linkVisibilityMenuItem}
                    onClick={() => handleLinkVisibilityChange(false)}
                  >
                    <Box className={classes.linkVisibilityMenuItemContent}>
                      <RestrictedAccessIcon
                        sx={{
                          width: 16,
                          height: 16,
                          ml: '1px',
                        }}
                      />
                      <span className={classes.linkVisibilityMenuItemText}>Only people added can view the link</span>
                    </Box>
                  </MenuItem>
                </Menu>
                {isShared && (
                  <Box className={classes.stopSharingContainer}>
                    <ButtonBase className={classes.stopSharingButton} onClick={handleStopSharing}>
                      Stop Sharing
                    </ButtonBase>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
